import { FeesAndCharges, LOAN_TYPE_ID } from '../../app/navigation/types';

export type RepaymentFrequency = 'weekly' | 'fortnightly' | 'monthly';

export const WEEKS_PER_YEAR = 52;
export const FORTNIGHTS_PER_YEAR = 26;
export const MONTHS_PER_YEAR = 12;

export interface LoanDetails {
    amount: number;
    loanTermsInMonth: number;
    repaymentFrequency: RepaymentFrequency;
}

export interface LoanRepayments {
    repaymentFrequency: RepaymentFrequency;
    loanTypeId: number;
    loanType: string;
    amount: number;
    cappedAmount: number;
    rate: number;
}

export const getLoanTypeById = (loanTypeId:number) => {
    switch (loanTypeId) {
    case LOAN_TYPE_ID.SECURED:
        return 'SecuredLoan';
    case LOAN_TYPE_ID.UNSECURED_FIXED:
        return 'FixedRate';
    case LOAN_TYPE_ID.UNSECURED_VARIABLE:
        return 'VariableRate';
    default:
        return 'VariableRate';
    }
};

export const getRepaymentPerWeek = (amount: number, frequency: string): number => {
    switch (frequency) {
    case 'weekly':
        return amount;
    case 'fortnightly':
        return (amount * FORTNIGHTS_PER_YEAR) / WEEKS_PER_YEAR;
    case 'monthly':
        return (amount * MONTHS_PER_YEAR) / WEEKS_PER_YEAR;
    default:
        break;
    }

    return amount;
};

const calculateRepayments = (
    loanAmount: number,
    loanTermsInMonth: number,
    interestRate: number,
    frequency: RepaymentFrequency,
    cappedAmount?: number,
) : number => {
    const monthlyInterestRate = interestRate / 1200.0;
    const realLoanAmount = cappedAmount ? Math.min(loanAmount, cappedAmount) : loanAmount;
    let repaymentAmount : number = (realLoanAmount * monthlyInterestRate * (1.0 + monthlyInterestRate) ** loanTermsInMonth)
        / ((1.0 + monthlyInterestRate) ** loanTermsInMonth - 1.0);

    switch (frequency) {
    case 'weekly':
        repaymentAmount = (repaymentAmount * MONTHS_PER_YEAR) / WEEKS_PER_YEAR;
        break;
    case 'fortnightly':
        repaymentAmount = (repaymentAmount * MONTHS_PER_YEAR) / FORTNIGHTS_PER_YEAR;
        break;
    default:
        break;
    }

    return Math.round(repaymentAmount);
};

const calculateLoanRepayments = (loanDetails: LoanDetails, feesAndCharges: FeesAndCharges[]) : LoanRepayments[] => {
    const repayments = feesAndCharges.map(({ loanTypeId, interestRate, cappedAmount }) => ({
        loanTypeId,
        loanType: getLoanTypeById(loanTypeId),
        amount: calculateRepayments(loanDetails.amount, loanDetails.loanTermsInMonth, interestRate, loanDetails.repaymentFrequency, cappedAmount),
        cappedAmount: cappedAmount ?? 0,
        repaymentFrequency: loanDetails.repaymentFrequency,
        rate: interestRate,
    }));

    return repayments;
};

export default calculateLoanRepayments;
